import React, { useState, FormEvent, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { FormGroup, InputGroup, Button, Tabs, Tab, HTMLSelect, HTMLTable, Alert } from "@blueprintjs/core";

import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import * as utils from "./utils";

import { useForm } from 'react-hook-form'

import { NumData } from './Types'

interface NumberViewProps {
    num: NumData,
    baseUrl?: string,
    token?: string | null,
    onEdit?: () => void,
    onNew?: () => void,
    onDelete?: (num: NumData) => void,
}

function NumberView({ num = {} as NumData, baseUrl = "", token = "", onEdit = () => { }, onNew = () => { }, onDelete = () => {} }: NumberViewProps) {
    const { t, i18n } = useTranslation();
    const [isOpenDelete, setIsOpenDelete] = useState(false)

    if (!("type" in num)) return null

    return (
        <div>
            <Alert icon="trash" intent="danger" confirmButtonText={t('button_delete')} isOpen={isOpenDelete} onConfirm={() => {onDelete(num); setIsOpenDelete(false)}} onCancel={() => setIsOpenDelete(false)} cancelButtonText={t('button_no')} canEscapeKeyCancel={true} canOutsideClickCancel={true}>
                <p>{t('confirm_delete_num', {numType: t(utils.numTypeName(num.type)), numGroup: num.group})}</p>
            </Alert>
            <div className="Row">
                <Button className="Col-1-1" icon="add" onClick={() => onNew()}>{t('new')}</Button>
                <Button className="Col-2-1" icon="edit" onClick={() => onEdit()}>{t('edit')}</Button>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('type')}>
                    <InputGroup readOnly value={t(utils.numTypeName(num.type))} />
                </FormGroup>
                <FormGroup className="Col-2-1" label={t('group')}>
                    <InputGroup readOnly value={num.group} />
                </FormGroup>
            </div>
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('last_number')}>
                    <InputGroup readOnly value={utils.formatNumber(num.last)} />
                </FormGroup>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <Button className="Col-1-1" icon="trash" onClick={() => setIsOpenDelete(!isOpenDelete)}>{t('delete')}</Button>
            </div>
        </div>
    )
}

interface NumberEditProps {
    num?: NumData,
    isNew?: boolean,
    onSave?: (num: NumData) => void,
    onCancel?: () => void;
}

// onChange={utils.handleStringChange(s => setAc(prevState => Object.assign(prevState, {acName1: s})))}
function NumberEdit({ num = {} as NumData, isNew = false, onSave = () => { }, onCancel = () => { } }: NumberEditProps) {
    const { t, i18n } = useTranslation();
    const { register, handleSubmit, reset, setValue } = useForm<NumData>()

    useEffect(() => {
        // Call reset to set default values if num is passed
        if ("type" in num) {
            reset(num)
        }
    }, [num, reset])

    useEffect(() => {
        // Suggest don't have ref to use with react-hook-form, so register it manually and call setValue when item is selected
        register({ name: "type" })
    }, [register])

    if (!isNew && !("type" in num)) return null

    const onSubmit = (data: NumData) => {
        //console.log("onsubmit called", data)
        onSave(data)
    }

        const handleHotkey = (e: React.KeyboardEvent<HTMLFormElement>) => {
        //console.log(`ctrl:${e.ctrlKey} alt:${e.altKey} shift:${e.shiftKey} meta:${e.metaKey} key:${e.key} keyCode:${e.keyCode}`)

        // ctrl + s
        if (e.ctrlKey && e.keyCode === 83) {
            e.preventDefault()
            handleSubmit(onSubmit)()
        }
    }

    /*<form onSubmit={handleSubmit(onSubmit)}>*/
    return (
        <form onKeyDown={handleHotkey}>
            <div className="Row">
                <Button className="Col-1-1" icon="cross" onClick={() => onCancel()}>{t('cancel')}</Button>
                <Button className="Col-2-1" icon="tick" onClick={handleSubmit(onSubmit)}>{t('save')}</Button>
            </div>
            <div className="Spacing-V-16" />
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('type')} labelFor="inputType">
                    {/*<InputGroup readOnly={!isNew} id="inputType" name="type" inputRef={register} />*/}
                    {isNew && <HTMLSelect fill defaultValue={num.type} onChange={(e) => setValue("type", e.currentTarget.value)}>
                        <option selected>{t('choose_an_item')}</option>
                        <option value="J">{t('journal')}</option>
                    </HTMLSelect>}
                    {!isNew && <InputGroup readOnly defaultValue={t(utils.numTypeName(num.type))} />}
                </FormGroup>
                <FormGroup className="Col-2-1" label={t('group')} labelFor="inputGroup">
                    <InputGroup readOnly={!isNew} id="inputGroup" name="group" inputRef={register} />
                </FormGroup>
            </div>
            <div className="Row">
                <FormGroup className="Col-1-1" label={t('last_number')} labelFor="inputLastNumber">
                    <InputGroup type="number" id="inputLastNumber" name="last" inputRef={register} />
                </FormGroup>
            </div>
        </form>
    )
}

interface NumberMainProps {
    svc: AxiosInstance
}

function NumberMain({ svc }: NumberMainProps) {
    const { t, i18n } = useTranslation();
    let { tabId: defaultTabId = "list" }: { tabId: string } = useParams()
    const history = useHistory()

    const [data, setData] = useState([] as NumData[])

    const [searchKey, setSearchKey] = useState("type")
    const [searchValue, setSearchValue] = useState("")

    const [selectedTabId, setSelectedTabId] = useState(defaultTabId) //useState("list")
    const [lastTabId, setLastTabId] = useState(defaultTabId) //useState("list")
    const [currentNum, setCurrentNum] = useState({} as NumData)

    const [currentMode, setCurrentMode] = useState("show")

    useEffect(() => {
        setSelectedTabId(defaultTabId)
    }, [defaultTabId])

    const changeTab = (tabId: string) => {
        history.push(`/numbers/${tabId}`)
        setSelectedTabId(tabId)
    }

    useEffect(() => {
        if (data.length > 0) {
            let i = 0;
            if ("type" in currentNum) {
                i = data.findIndex(n => n.type === currentNum.type && n.group === currentNum.group)
            }
            setCurrentNum(i > 0 ? data[i] : data[0])
        }
    }, [data])

    const refreshNumbers = async () => {
        try {
            const res = await svc.post('/numlist',
                {
                    type: searchValue,
                });

            setData(res.data.data)
            if (res.data.data.length === 0) {
                utils.showError(t('no_record'))
            }
            /*
            if (res.data.data.length > 0) {
                setCurrentNum(res.data.data[0])
            } else {
                utils.showError(t('no_record'))
            }
            */
            //changeTab("list")
        } catch (error) {
            utils.showError(error)
        }
    }

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        refreshNumbers()
    }

    const editOnSave = async (num: NumData) => {
        try {
            const res = await svc.post('/numupdate', num)
            utils.showSuccess(t('saved', {key: res.data.data.group}))
            for (let i = 0; i < data.length; i++) {
                if (data[i].type === res.data.data.type && data[i].group === res.data.data.group) {
                    data[i] = { ...data[i], ...res.data.data }
                    setData(data)
                    //setCurrentNum(res.data.data)
                    break
                }
            }
            setCurrentNum(res.data.data)
            setCurrentMode("show")
        } catch (error) {
            utils.showError(error)
        }
    }

    const newOnSave = async (num: NumData) => {
        try {
            const res = await svc.post('/numadd', num)
            utils.showSuccess(t('saved', {key: res.data.data.group}))
            refreshNumbers()
            /*
            for (let i = 0; i < data.length; i++) {
                if (data[i].type === res.data.data.type && data[i].group === res.data.data.group) {
                    data[i] = { ...data[i], ...res.data.data }
                    setData(data)
                    break
                }
            }
            */
            setCurrentNum(res.data.data)
            setCurrentMode("show")
        } catch (error) {
            utils.showError(error)
        }
    }

    const viewOnDelete = async (num: NumData) => {
        try {
            //console.log("deleting num:", num)
            const res = await svc.post('/numdelete', num)
            utils.showSuccess(t('deleted'))
            setData(data.filter(num => !(num.type === res.data.type && num.group === res.data.group)))
            setCurrentNum({} as NumData)
            changeTab("list")
        } catch (error) {
            utils.showError(error)
        }
    }

    // currentMode = show
    let detail = <NumberView num={currentNum} baseUrl={svc.defaults.baseURL} token={localStorage.getItem('token')} onEdit={() => setCurrentMode("edit")} onNew={() => {setLastTabId(selectedTabId); setCurrentMode("new")}} onDelete={viewOnDelete}></NumberView>
    switch (currentMode) {
        case "edit":
            detail = <NumberEdit num={currentNum} onSave={editOnSave} onCancel={() => setCurrentMode("show")}></NumberEdit>
            break
        case "new":
            detail = <NumberEdit isNew onSave={newOnSave} onCancel={() => { setCurrentMode("show"); changeTab(lastTabId) }}></NumberEdit>
            break
    }

    let list = <>
        <div className="Row">
            <Button className="Col-1-1" icon="add" onClick={() => { setLastTabId(selectedTabId); setCurrentMode("new"); changeTab("detail") }}>{t('new')}</Button>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed>
            <thead>
                <tr>
                    <th className="TCol-Type">{t('type')}</th>
                    <th className="TCol-Group">{t('group')}</th>
                    <th className="TCol-Number"><div className="Align-R">{t('last_number')}</div></th>
                </tr>
            </thead>
            <tbody>
                {data.map((num, i) =>
                    <tr key={i} onDoubleClick={() => { changeTab("detail") }}>
                        <td className="TCol-Type" onClick={() => { setCurrentNum(num) }}>{t(utils.numTypeName(num.type))}</td>
                        <td className="TCol-Group" onClick={() => { setCurrentNum(num) }}>{num.group}</td>
                        <td className="TCol-Number" onClick={() => { setCurrentNum(num) }}><div className="Align-R">{utils.formatNumber(num.last)}</div></td>
                    </tr>
                )}
            </tbody>
        </HTMLTable>
    </>

    return (
        <>
            <div style={{fontSize: "21px", fontWeight: 700}}>{t('numbers')}</div>
            <div className="Spacing-V-16"></div>
            <form onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('type')} labelFor="inputSearchValue">
                        <HTMLSelect fill onChange={(e) => setSearchValue(e.currentTarget.value)}>
                            <option selected value="J">{t('journal')}</option>
                        </HTMLSelect>
                    </FormGroup>
                    <Button className="Col-2-1 Button-LabeledFormGroup" icon="refresh" type="submit">{t('refresh')}</Button>
                </div>
                {/*
                <div className="Row">
                    <FormGroup className="Col-1-3" label="Search" labelFor="inputSearchValue">
                        <ControlGroup fill>
                            <HTMLSelect className={Classes.FIXED} value={searchKey} onChange={utils.handleStringChange(s => setSearchKey(s))}>
                                <option value="type">Type</option>
                            </HTMLSelect>
                            <InputGroup
                                id="inputSearchValue"
                                placeholder="Search"
                                value={searchValue}
                                onChange={utils.handleStringChange(s => setSearchValue(s))}
                                leftIcon="search"
                                rightElement={<Button icon={IconNames.ARROW_RIGHT} minimal type="submit"></Button>}
                            />
                        </ControlGroup>
                    </FormGroup>
                </div>
                */}
            </form>
            <Tabs onChange={(newTabId) => changeTab(newTabId as string)} selectedTabId={selectedTabId}>
                <Tab id="list" title={t('list')} panel={list} />
                <Tab id="detail" title={t('detail')} panel={detail} />
            </Tabs>
        </>
    );
}

export default NumberMain